/**
 * PERSISTENT TABLE DATA HOOK
 * Extends useTableData with localStorage persistence
 * Automatically saves and restores form data when switching between sheets
 * @version 1.0.0
 */

import { useEffect, useRef } from 'react';
import { useTableData, UseTableDataReturn } from './useTableData';
import { TableData } from '../types';

interface UsePersistentTableDataOptions {
  storageKey: string; // Unique key for localStorage (e.g., 'masterSheet_unsaved_data')
  enabled?: boolean; // Whether to enable persistence (default: true)
  debounceMs?: number; // Debounce save operations (default: 500ms)
}

const hasMeaningfulContent = (data: TableData, rowKeys: string[]): boolean => {
  return rowKeys.some((key: string) => {
    const row = data[key];
    if (!row) return false;
    return Object.keys(row).some(k => {
      const keyLower = k.toLowerCase();
      if (keyLower === 'entry_date' || keyLower === 'date' || keyLower === 'id') {
        return false;
      }
      const value = row[k];
      return value !== null && value !== undefined && value !== '' && value !== '0';
    });
  });
};

const restoreFromStorage = (
  storageKey: string,
  fallback: { data: TableData; rowKeys: string[] }
): { data: TableData; rowKeys: string[] } => {
  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed?.data && parsed?.rowKeys && parsed.rowKeys.length > 0) {
        if (hasMeaningfulContent(parsed.data, parsed.rowKeys)) {
          return {
            data: parsed.data,
            rowKeys: parsed.rowKeys
          };
        }
localStorage.removeItem(storageKey);
      }
    }
  } catch (error) {
}
  return fallback;
};

export const usePersistentTableData = (
  initialData: TableData = {},
  initialRowKeys: string[] = [],
  options: UsePersistentTableDataOptions
): UseTableDataReturn => {
  const { storageKey, enabled = true, debounceMs = 500 } = options;
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRestoringRef = useRef(false);
  const previousStorageKeyRef = useRef(storageKey);

  const initialSnapshot = enabled
    ? restoreFromStorage(storageKey, { data: initialData, rowKeys: initialRowKeys })
    : { data: initialData, rowKeys: initialRowKeys };

  const tableData = useTableData(initialSnapshot.data, initialSnapshot.rowKeys);

  const originalClearData = tableData.clearData;
  const clearData = () => {
    if (enabled) {
      try {
        localStorage.removeItem(storageKey);
      } catch (error) {
}
    }
    originalClearData();
  };

  const originalSetAllData = tableData.setAllData;
  const setAllData = (newData: TableData, newRowKeys: string[]) => {
    isRestoringRef.current = true;
    originalSetAllData(newData, newRowKeys);
    setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);
  };

  // Auto-save to localStorage whenever data changes
  useEffect(() => {
    if (!enabled || isRestoringRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        const dataToSave = {
          data: tableData.data,
          rowKeys: tableData.rowKeys,
          timestamp: Date.now()
        };
        localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      } catch (error) {
}
    }, debounceMs);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [tableData.data, tableData.rowKeys, storageKey, enabled, debounceMs]);

  // Reload persisted data whenever the storageKey changes (user/site switched)
  useEffect(() => {
    if (!enabled) return;
    if (previousStorageKeyRef.current === storageKey) return;

    previousStorageKeyRef.current = storageKey;
    const snapshot = restoreFromStorage(storageKey, { data: {}, rowKeys: [] });
    setAllData(snapshot.data, snapshot.rowKeys);
  }, [storageKey, enabled, setAllData]);

  return {
    ...tableData,
    clearData,
    setAllData
  };
};



